/**
 * `/start` — the get-started chooser (#1720, reshaped on founder
 * direction 2026-08-01 round 4).
 *
 * **The thing this fixes.** Before this page existed, an authenticated
 * account with no team landed on the `HomePage` zero-team empty state,
 * whose only action was "Start onboarding" — i.e. *found a new tenant*.
 * A member who signed up before their lead's invite arrived was quietly
 * funnelled into creating their own team, with no affordance anywhere
 * saying "you're probably meant to join one". The branch is now
 * explicit: two doors, named, illustrated, and the person picks.
 *
 * **Round 4 reshape.** The founder wants the landing's Sign up button
 * to lead HERE, before auth — a dedicated two-option page (the Timble
 * get-started pattern: two illustrated choices + an "already have an
 * account?" escape), drawn in our own register rather than borrowed
 * graphics. So the page now serves BOTH moments:
 *
 *   - **Unauthenticated** (landing → Sign up): each door stashes its
 *     `tarii.signup_intent` ("create" / "join") and continues to
 *     `/sign-up`; post-auth routing (`useZeroTeamStartTarget`) honours
 *     the stash and skips re-asking. An "Already have an account?
 *     Sign in" line sits under the heading.
 *   - **Authenticated, zero teams** (routed here by `<HomeEntryGate/>`):
 *     the doors go straight to `/onboarding` / `/join` — no stash, no
 *     Clerk hop, and the signed-in email footer renders.
 *
 * **Dev-shim degradation:** without a Clerk key `/sign-up` is a
 * redirect to `/sign-in`, so an unauthenticated visitor here is bounced
 * to `/sign-in` outright (same posture as `SignUpGate`) — the doors
 * would otherwise be buttons to a redirect. Authenticated shim
 * accounts still get the chooser.
 *
 * **Where it sits.** Mounted OUTSIDE the auth gate (it must render for
 * logged-out visitors) and outside the AppShell — a first-run decision
 * surface, not a chrome surface. It borrows the landing's register
 * (mono micro-labels, hairline rules, one tight radius) but NOT its
 * sage-clinic pin — by the authenticated moment it is an in-app surface
 * and renders in the visitor's own theme.
 *
 * **#2071 S4 — the laptop way in** (design note §3.4, J3). Someone who
 * joined their team from a Telegram link has no login; signing in here
 * with Google makes a new, empty account, which lands on this rung. So the
 * zero-team rung (signed in, no team, Telegram on) carries one quiet line
 * under the doors: *Joined your team from a Telegram link?* with **Connect
 * your Telegram** — the existing Connect Telegram page (QR on a laptop,
 * `t.me` link). When they tap Start, the bot asks to add this login to
 * their Telegram account; the page then reloads into their team. Never
 * shown to an account that already has a team, nor before sign-in.
 *
 * **Focal hierarchy** (`feedback_ui_section_focal_component.md`): the
 * two doors are NOT an equal-weight pair. "Set up your team" carries
 * the accent rule and the primary button; "Join your team" sits beside
 * it at quieter weight. Both are one click; only one is loud. Each door
 * carries one drawn, product-truthful artifact (divs + tokens, no stock
 * art, no emoji): a fresh roster board for the lead door, a team ring
 * with an open seat for the member door.
 */
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { useProductOffers } from "../api/useProductOffers";
import { useTelegramLink } from "../api/useTelegramLink";
import { useWorkspaces } from "../api/useWorkspaces";

import { Logo } from "../components/Logo";
import { Button } from "../components/ui";
import { useAuth } from "../lib/AuthContext";
import { hasClerk } from "../lib/clerk";
import {
  INVITE_USED_LINE,
  clearInviteCode,
  redeemInvite,
  storeInviteCode,
} from "../lib/productInvite";
import { type ProductKey, productByKey } from "../lib/products";
import { type SignupIntent, stashSignupIntent } from "../lib/signupIntent";
import { stashSignupProduct } from "../lib/signupProduct";
import { useClaimStoredInvite } from "../lib/useClaimStoredInvite";
import { ProductQuestion } from "./ProductQuestion";

/**
 * The lead door's drawing — a fresh roster board: day header, a few
 * rows taking shape, the last cells still open. Setting up = a board
 * waiting to be run (deliberately no flagged gap here).
 */
function ArtNewBoard() {
  const rows: ReadonlyArray<ReadonlyArray<"mid" | "light" | "heavy" | "open">> = [
    ["mid", "light", "heavy", "mid", "light"],
    ["light", "heavy", "mid", "light", "open"],
    ["heavy", "mid", "light", "open", "open"],
  ];
  const cellClass = {
    heavy: "bg-accent",
    mid: "bg-accent/60",
    light: "bg-accent/30",
    open: "border border-dashed border-border-strong",
  } as const;
  return (
    <div aria-hidden="true" className="flex flex-col gap-[5px]">
      <div className="flex gap-[5px]">
        {["M", "T", "W", "T", "F"].map((day, d) => (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: static day header (labels repeat)
            key={d}
            className="flex h-4 w-5 items-center justify-center font-mono text-[9px] text-fg-faint uppercase"
          >
            {day}
          </span>
        ))}
      </div>
      {rows.map((row, r) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: static presentational grid
          key={r}
          className="flex gap-[5px]"
        >
          {row.map((cell, c) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: static presentational grid
              key={c}
              className={`h-5 w-5 rounded-[3px] ${cellClass[cell]}`}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The member door's drawing — the team, with your seat open: a row of
 * member circles and one dashed open circle the arrow walks into.
 */
function ArtOpenSeat() {
  return (
    <div aria-hidden="true" className="flex items-center">
      <div className="flex">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`h-8 w-8 rounded-full border-2 border-bg bg-accent/60 ${i > 0 ? "-ml-2" : ""}`}
          />
        ))}
      </div>
      <ArrowRight className="mx-2.5 h-3.5 w-3.5 text-fg-faint" />
      <span className="h-8 w-8 rounded-full border border-dashed border-border-strong" />
    </div>
  );
}

type Door = {
  intent: SignupIntent;
  kicker: string;
  title: string;
  body: string;
  cta: string;
  authedTo: string;
  art: () => JSX.Element;
  testId: string;
};

const DOORS: readonly [Door, Door] = [
  {
    intent: "create",
    kicker: "For leads and planners",
    title: "Set up your team",
    body: "Name it, invite the people in it, set the rules it runs on.",
    cta: "Start setup",
    authedTo: "/onboarding",
    art: ArtNewBoard,
    testId: "start-choose-create",
  },
  {
    intent: "join",
    kicker: "For invited members",
    title: "Join your team",
    body: "Joining runs on an invite link from your lead.",
    cta: "I have an invite",
    authedTo: "/join",
    art: ArtOpenSeat,
    testId: "start-choose-join",
  },
];

/** #2071 S4 — the zero-team rung's Telegram line. */
export const TELEGRAM_JOINED_LINE = "Joined your team from a Telegram link?";
export const TELEGRAM_CONNECT_CTA = "Connect your Telegram";

/**
 * The zero-team rung: signed in, the workspace list read and empty. Only
 * then may the Telegram line show (never for an account with a team).
 */
function useZeroTeamRung(authed: boolean): boolean {
  const workspaces = useWorkspaces();
  const telegram = useTelegramLink(authed);
  return (
    authed &&
    Array.isArray(workspaces.data) &&
    workspaces.data.length === 0 &&
    telegram.available === true
  );
}

export function StartPage() {
  const { account, accountId, loading, refresh } = useAuth();
  const authed = Boolean(accountId);
  const zeroTeamTelegram = useZeroTeamRung(authed);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isOffered = useProductOffers();

  // #2630 — "Set up your team" opens the product question first.
  // `/start?product=<key>` opens it with that answer picked.
  const fromUrl = productByKey(searchParams.get("product"))?.key ?? null;
  const [asking, setAsking] = useState(fromUrl !== null);
  const [picked, setPicked] = useState<ProductKey | null>(fromUrl);

  const [note, setNote] = useState("");

  // #2652 — `/start?invite=<code>`: keep the code, tell the API, then open
  // the question with that product's answer open. A bad code shows one line
  // and the normal start.
  const inviteCode = searchParams.get("invite");
  const [inviteRefused, setInviteRefused] = useState(false);
  // A late sign-in claims the stored code through this hook.
  useClaimStoredInvite();
  const startedFor = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once per code
  useEffect(() => {
    if (!inviteCode || startedFor.current === inviteCode) return;
    startedFor.current = inviteCode;
    storeInviteCode(inviteCode);
    redeemInvite(inviteCode).then(async (key) => {
      const product = productByKey(key);
      if (product && authed) {
        // Signed in: the redeem tied the code to the account. Read the
        // account again so `products_offered` carries the product.
        await refresh({ silent: true });
        clearInviteCode();
      }
      if (!product) {
        clearInviteCode();
        setInviteRefused(true);
        return;
      }
      setPicked(product.key);
      setAsking(true);
    });
  }, [inviteCode]);

  // #2654 — until two products are open, nobody sees the question: "Start
  // setup" goes straight to the Shift rosters start.
  const askQuestion = isOffered.askQuestion;

  const onConfirmProduct = (key: ProductKey) => {
    stashSignupProduct(key, note);
    if (authed) {
      navigate("/onboarding");
      return;
    }
    stashSignupIntent("create");
    navigate("/sign-up");
  };

  // Dev-shim + logged out: `/sign-up` is a redirect to `/sign-in`, so
  // both doors would be too — bounce like `SignUpGate` does.
  if (!hasClerk && !loading && !authed) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto w-full max-w-3xl px-5 sm:px-8">
        <header className="flex items-center justify-between gap-4 border-border border-b py-5">
          <Link to="/" aria-label="Tarii home" className="inline-flex min-h-tap items-center">
            <Logo className="h-7" />
          </Link>
          <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
            Getting started
          </span>
        </header>

        <main className="pt-8 pb-12 sm:pt-10 sm:pb-16">
          {asking && (askQuestion || (picked !== null && isOffered(picked))) ? (
            <ProductQuestion
              picked={picked}
              isOffered={isOffered}
              onPick={(key, text) => {
                setNote(text ?? "");
                setPicked(key);
              }}
              onBack={() => {
                // One press: back to the question from a confirmation, else to the doors.
                if (picked && isOffered(picked)) setPicked(null);
                else {
                  setPicked(null);
                  setAsking(false);
                }
              }}
              onConfirm={onConfirmProduct}
            />
          ) : (
            <>
              {inviteRefused ? (
                <p className="mb-4 text-fg-muted text-sm" data-testid="start-invite-refused">
                  {INVITE_USED_LINE}
                </p>
              ) : null}
              <h1 className="max-w-[18ch] font-semibold text-[2rem] text-fg leading-[1.1] tracking-tight sm:text-[2.5rem]">
                How do you want to start?
              </h1>
              {authed ? (
                <p className="mt-3 max-w-[56ch] text-base text-fg-muted leading-relaxed">
                  Start a team, or join one you've been invited to.
                </p>
              ) : (
                <p className="mt-3 text-base text-fg-muted leading-relaxed">
                  Already have an account?{" "}
                  <Link
                    to="/sign-in"
                    className="inline-flex min-h-tap items-center text-accent-fg underline underline-offset-4"
                  >
                    Sign in
                  </Link>
                </p>
              )}

              {/* The two doors. Focal: the lead door — accent top rule +
              primary action; the member door sits beside it at quieter
              weight. Each carries its drawn artifact; the whole block
              is one bordered surface split by a hairline, not two
              floating cards. */}
              <div className="mt-9 grid overflow-hidden rounded-md border border-border-strong sm:grid-cols-2">
                {DOORS.map((door, index) => {
                  const focal = index === 0;
                  const Art = door.art;
                  return (
                    <div
                      key={door.intent}
                      data-testid={door.testId}
                      className={`flex flex-col items-start gap-5 p-6 sm:p-7 ${
                        focal
                          ? "border-border border-b border-t-2 border-t-accent sm:border-b-0 sm:border-r"
                          : "border-t-2 border-t-transparent"
                      }`}
                    >
                      <div className="flex h-24 items-center">
                        <Art />
                      </div>
                      <div>
                        <span className="font-mono text-[10px] text-fg-subtle uppercase tracking-[0.18em]">
                          {door.kicker}
                        </span>
                        <h2 className="mt-2 font-semibold text-fg text-xl tracking-tight">
                          {door.title}
                        </h2>
                        <p className="mt-1.5 max-w-[38ch] text-fg-muted text-sm leading-relaxed">
                          {door.body}
                        </p>
                      </div>
                      <Button
                        asChild
                        size="md"
                        variant={focal ? "primary" : "secondary"}
                        className="mt-auto h-11"
                      >
                        {door.intent === "create" ? (
                          <button
                            type="button"
                            onClick={() =>
                              askQuestion ? setAsking(true) : onConfirmProduct("shifts")
                            }
                          >
                            {door.cta}
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                          </button>
                        ) : (
                          <Link
                            to={authed ? door.authedTo : "/sign-up"}
                            onClick={authed ? undefined : () => stashSignupIntent(door.intent)}
                          >
                            {door.cta}
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                          </Link>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {zeroTeamTelegram ? (
                <div
                  className="mt-6 flex flex-col gap-3 border-border border-b pb-6 sm:flex-row sm:items-center sm:justify-between"
                  data-testid="start-telegram-joined"
                >
                  <p className="text-fg-muted text-sm">{TELEGRAM_JOINED_LINE}</p>
                  <Button asChild size="sm" variant="secondary" className="h-11">
                    <Link to="/connect/telegram" data-testid="start-telegram-connect">
                      {TELEGRAM_CONNECT_CTA}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </div>
              ) : null}

              <p className="mt-7 text-fg-subtle text-xs leading-relaxed">
                {authed && account?.email ? (
                  <>
                    Signed in as <span className="font-mono text-fg-muted">{account.email}</span> —
                    invites are sent to this address.
                  </>
                ) : (
                  "Invites are sent to the email address on your account."
                )}
              </p>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
